<template>
  <div id="app">
    <div v-if="this.mobile" class="main-mobile">
      <Menu :infos="this.infoBoxes" style="clear:right" ref="menu"></Menu>
      <header class="nav-header">
        <a v-if="!scrolled" class="right" tabindex="0" role="button" @keydown.enter="toggleMenu" @keydown.space="toggleMenu" @click="toggleMenu">
          <img
            alt="Button zum Öffnen der Navigation"
            src="./assets/icons/BtnMenu.svg">
        </a>
        <a v-else class="right" tabindex="0" role="button" @keydown.space="toggleMenu" @keydown.enter="toggleMenu" @click="toggleMenu">
          <img
            alt="Button zum Öffnen der Navigation"
            src="./assets/icons/BtnMenuSmall.svg">
        </a>
      </header>
      <article>
        <h1 class="main-header">Die nächste Generation im Mobilfunk: 5G</h1>
        <img class="overview-image" alt="Übersicht" src="./assets/MainImage.jpg">
        <p>
          5G ist die nächste Mobilfunkgeneration. Sie ermöglicht größere Datenübertragungsraten und neue Technologien.
          Manche fragen sich, ob der 5G-Ausbau ein gesundheitliches Risiko nach sich zieht. Wie sind gesundheitliche
          Wirkungen zu bewerten und welche technischen Möglichkeiten bietet 5G?
        </p>
      </article>
      <info-box v-for="info in this.infoBoxes" v-bind:info="info" :key="info.imageUrl" style="clear: both">
      </info-box>

      <hr>
      <p class="footer">
        &copy;<a href="https://www.bfs.de/" target="_blank">Bundesamt für Strahlenschutz</a>
        <span class="right"><a href="https://www.bfs.de/DE/service/impressum/impressum_node.html" target="_blank">Impressum</a></span> <br>
        <a href="https://www.infografik.pro" target="_blank">Design: INFOGRAFIK PRO</a>
      </p>
      <div id='safari-footer'>
      </div>

      <footer class="nav-footer">
        <a href="https://twitter.com/strahlenschutz/" target="_blank">
          <img alt="Twitter Share Button" src="./assets/icons/BtnTwit.svg">
        </a>
        <a v-if="!this.scrolled" href="#Automatisiertes-Fahren" class="right">
          <img alt="Button für Navigation" src="./assets/icons/BtnDown.svg">
        </a>
        <a v-else class="right" href="#">
          <img alt="Button für Navigation nach oben" src="./assets/icons/BtnUp.svg">
        </a>
      </footer>
    </div>

    <div v-else class="main-desktop">
      <div class="desktop-header">
        <h1 class="main-header">Die nächste Generation im Mobilfunk: 5G</h1>
        <img src="./assets/LogoBfS.svg" alt="Logo des Bundesamts für Strahlenschutz">
      </div>

      <div class="main-svg" ref="svg">
        <MainSVG/>
      </div>

      <article v-if="this.currentScene" class="info-overlay" :class="this.currentScene.position">
        <div class="info-header">
          <a class="right" tabindex="0" role="button" @click="this.closeTextBox" @keydown.enter="closeTextBox" @keydown.space="closeTextBox">
            <img alt="Schließen der Navigation" src="./assets/icons/BtnClose.svg">
          </a>
          <h2 class="sub-header">{{ this.currentScene.info.headline }}</h2>
          <img class="sub-image" :src="this.currentScene.info.imageUrl" :alt="this.currentScene.info.imageAlt">
        </div>
        <div v-html="this.currentScene.info.content" class="info-content"></div>
      </article>

      <div class="boxes">
      <div v-show="showHint" class="hint-box">
        <a class="right" tabindex="0" role="button" @click="showHint = !showHint" @keydown.enter="showHint = !showHint" @keydown.space="showHint = !showHint">
          <img alt="Schließen des Hinweises" src="./assets/icons/BtnClose.svg">
        </a>
        <p>
          Die Verbindungen zwischen Endgeräten und Sendemasten ist hier schematisch dargestellt. In Wirklichkeit sind
          die Felder nicht scharf abgegrenzt, sondern umgeben uns (außer im Funkloch) überall.
        </p>
        <p><a href="https://www.bfs.de/DE/themen/emf/kompetenzzentrum/mobilfunk/basiswissen/basiswissen_node.html"
              target="_blank">Mehr zu den Grundlagen des Mobilfunks.</a></p>
      </div>
      <div class="boxes-gap"></div>
      <div class="legend-box">
        <h2 class="sub-header">Legende</h2>
        <p>Bewegen Sie Ihre Maus über die Grafik und entdecken Sie Informationen zu:</p>
        <ul>
          <li>Sendemasten und Frequenzen</li>
          <li>Wirkungen</li>
          <li>Vorsorge</li>
          <li>Industrie und Landwirtschaft</li>
          <li>Gesundheit</li>
          <li>Automatisiertem Fahren</li>
        </ul>
      </div>
      </div>

      <div class="desktop-footer">
        <a href="https://www.infografik.pro" target="_blank">Design: INFOGRAFIK PRO</a>
        <a tabindex="0" role="button" @click="showHint = !showHint" @keydown.enter="showHint = !showHint"  @keydown.space="showHint = !showHint">
          <img alt="Öffnen zusätzlicher Hinweise" src="./assets/icons/BtnInfo.svg">
        </a>
        <a href="https://twitter.com/strahlenschutz/" target="_blank">
          <img alt="Twitter Share Button" src="./assets/icons/BtnTwit.svg">
        </a>
        <a href="https://www.bfs.de/DE/service/impressum/impressum_node.html" target="_blank">Impressum</a>
      </div>
    </div>
  </div>
</template>

<script>
import { infoBoxes, beams, clickareas, outlines, positions } from '@/data/infos'
import Menu from '@/components/Menu'
import InfoBox from '@/components/InfoBox'
import Scene from '@/models/scene'
import MainSVG from '@/assets/MainImage.svg?inline'

export default {
  name: 'App',
  data () {
    const scenes = []
    for (let i = 0; i < beams.length; i++) {
      const scene = new Scene(infoBoxes[i], clickareas[i], beams[i], outlines[i], positions[i])
      scenes.push(scene)
    }
    return {
      scrolled: false,
      mobile: true,
      // If the window size is > maxMobileWidth we render the desktop version
      // Written as a media query, because we use window.matchMedia(this.maxMobileWidth) to determine which version to render
      maxMobileWidth: ['(max-width: 767px)', '(max-height: 640px)'],
      infoBoxes: infoBoxes,
      scenes: scenes,
      currentScene: null,
      svg: null,
      hintIntervalID: null,
      restartHintInterval: null,
      showHint: false
    }
  },
  methods: {
    toggleMenu () {
      this.$refs.menu.open()
    },
    handleScroll () {
      this.scrolled = window.scrollY > 320
    },
    handleResize () {
      this.mobile = window.matchMedia(this.maxMobileWidth).matches
    },
    closeTextBox () {
      this.hideBeamerRect()
      this.hideScene(this.currentScene)
      this.currentScene = null
      this.restartHintInterval = window.setTimeout(() => {
        if (!this.currentScene) {
          this.sceneHint()
        }
      }, 10000)
    },
    hideBeamerRect () {
      const blenderBeamRect = this.svg.getElementById('BlenderBeams')
      blenderBeamRect.setAttribute('visibility', 'hidden')
      blenderBeamRect.classList.remove('fade-in')
      return blenderBeamRect
    },
    // TODO: Move methods to Scene
    sceneHint () {
      const timeToStartAnimation = 100
      const timeBetweenAnimations = 4000

      const animationFuncs = [this.animateBeam, this.animateClickarea]
      let index = 0

      const animate = () => {
        this.scenes.forEach(s => {
          animationFuncs[index](s)
        })
        index = index === 0 ? 1 : 0
        this.hintIntervalID = window.setTimeout(animate, timeBetweenAnimations)
      }

      this.hintIntervalID = window.setTimeout(() => {
        animate()
      }, timeToStartAnimation)
    },
    animateBeam (scene) {
      const beam = this.svg.getElementById(scene.beam)
      beam.setAttribute('style', 'opacity: 0;')
      beam.setAttribute('visibility', 'visible')
      beam.classList.add('fadeInOut')
      const outline = this.svg.getElementById(scene.outlines)
      outline.setAttribute('style', 'opacity: 0;')
      outline.setAttribute('visibility', 'visible')
      outline.classList.add('fadeInOut')

      beam.addEventListener('animationend', () => {
        beam.setAttribute('visibility', 'hidden')
        beam.classList.remove('fadeInOut')
        outline.setAttribute('visibility', 'hidden')
        outline.classList.remove('fadeInOut')
      })
    },
    animateClickarea (scene) {
      scene.clickareas.forEach(c => {
        if (!c.animate) return
        const clickarea = this.svg.getElementById(c.id)
        clickarea.setAttribute('style', 'opacity: 0;')
        clickarea.setAttribute('visibility', 'visible')
        clickarea.classList.add('fadeInOut')

        clickarea.addEventListener('animationend', () => {
          clickarea.classList.remove('fadeInOut')
        })
      })
    },
    hideScene (scene) {
      const beam = this.svg.getElementById(scene.beam)
      beam.setAttribute('pointer-events', 'none')
      beam.setAttribute('visibility', 'hidden')
      const outline = this.svg.getElementById(scene.outlines)
      outline.setAttribute('visibility', 'hidden')
    },
    showScene (scene) {
      const beam = this.svg.getElementById(scene.beam)
      beam.setAttribute('visibility', 'visible')
      beam.setAttribute('style', 'opacity: 1!important;')
      beam.style.animationPlayState = 'paused'
      const outline = this.svg.getElementById(scene.outlines)
      outline.setAttribute('visibility', 'visible')
      outline.setAttribute('style', 'opacity: 1!important;')
      outline.style.animationPlayState = 'paused'
    }
  },
  created () {
    this.mobile = this.maxMobileWidth.some(m => window.matchMedia(m).matches)
  },

  mounted () {
    // The SVG container does not exist and is therefore undefined in the mobile version.
    if (this.mobile) return

    const svg = this.$refs.svg.getElementsByTagName('svg')[0]
    const scenes = this.scenes
    const that = this

    this.svg = svg
    this.sceneHint()

    // We hide the blender beam rect, which overlays and darkens the whole SVG.
    // This enables us to register click events on the clickarea which would otherwise be caught by the rect.
    // It is only used after a click event on a clickarea.
    const blenderBeamRect = this.hideBeamerRect()
    blenderBeamRect.addEventListener('click', this.closeTextBox)

    scenes.forEach(s => {
      this.hideScene(s)

      s.clickareas.forEach(c => {
        const clickarea = svg.getElementById(c.id)

        // Initially hide the clickareas.
        // clickarea.setAttribute('visibility', 'hidden')
        // But listen to pointer events. Otherwise we could not register mouse events for hidden elements
        clickarea.setAttribute('pointer-events', 'all')
        clickarea.setAttribute('style', 'opacity: 0;')

        // Show clickareas on mouseover
        clickarea.addEventListener('mouseover', function () {
          c.animate = false
          clickarea.style.animationPlayState = 'initial'
          clickarea.setAttribute('style', 'opacity: 1!important;')
          clickarea.setAttribute('visibility', 'visible')
        })
        clickarea.addEventListener('mouseout', function () {
          c.animate = true
          // clickarea.style.animationPlayState = 'initial'
          clickarea.setAttribute('visibility', 'hidden')
        })
        clickarea.addEventListener('focusin', function () {
          clickarea.setAttribute('style', 'opacity: 1;')
        })
        clickarea.addEventListener('focusout', function () {
          clickarea.setAttribute('style', 'opacity: 0;')
        })
        // Show beams and outlines on click, darken the background and show info box.
        const events = ['click', 'keydown']
        events.forEach(event => {
          clickarea.addEventListener(event, function (e) {
            if (e.type === 'keydown' && !(e.code === 'Space' || e.code === 'Enter')) return
            clickarea.setAttribute('visibility', 'hidden')
            if (that.currentScene != null) {
              that.hideScene(that.currentScene)
            }
            that.showScene(s)
            blenderBeamRect.setAttribute('visibility', 'visible')
            blenderBeamRect.classList.add('fade-in')
            that.currentScene = s
            if (that.hintIntervalID) {
              window.clearTimeout(that.hintIntervalID)
              that.hintIntervalID = null
            }
            if (that.restartHintInterval) {
              window.clearTimeout(that.restartHintInterval)
              that.restartHintInterval = null
            }
          })
        })
      })
    })
  },
  beforeMount () {
    window.addEventListener('resize', this.handleResize)
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('scroll', this.handleScroll)
  },
  components: {
    InfoBox,
    Menu,
    MainSVG
  }
}

</script>
<style lang="css">
.fadeInOut {
  animation: 2s linear 1s 2 alternate fader;
}

@keyframes fader {
  from {
    opacity: 0;
  }
  75% {
    opacity: 1;
  }
  to {
    opacity: 1;
  }
}

.fade-in {
  animation: fade 0.225s;
}

.fade-out {
  animation: fade 1s ease reverse;
}

@keyframes fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 0.26;
  }
}
</style>
<style lang="sass">
$primary-color: #666666
$title-color: #28353D
$subtitle-color: #457AB0
$main-title-size: 19pt
$main-margin: 30px
$desktop-main-margin: 15px

html, body
  margin: 0
  height: 100%

hr
  color: $primary-color
  opacity: 0.6

#background-content
  overflow: hidden

.right
  float: right

#app
  font-family: Open Sans Regular, sans-serif
  -webkit-font-smoothing: antialiased
  -moz-osx-font-smoothing: grayscale
  text-align: left
  color: $primary-color
  line-height: 20pt
  height: 100%

  %headers
    font-family: Open Sans Condensed, sans-serif
    text-transform: uppercase
    line-height: 25pt
    color: $title-color
    font-size: $main-title-size
    font-weight: bold

    h1, h2
      font-size: 1em

    span
      color: $subtitle-color

  .main-header
    @extend %headers
    @media (max-width: 380px)
      margin-right: 1em

  .sub-header
    @extend %headers
    text-transform: none
    font-size: $main-title-size - 1
    line-height: 24pt

  .overview-image
    width: 100vw
    margin-left: -$main-margin
    margin-top: 20px

  .sub-image
    display: block
    margin: auto
    max-width: 100%

  .menu-header
    @extend %headers
    margin: $main-margin
    color: white

    span
      color: white

  .nav-header
    position: fixed
    left: 0
    top: 0
    width: 100%

    a
      margin-top: $main-margin
      margin-right: $main-margin

  .nav-footer
    position: fixed
    left: 0
    bottom: 0
    background: rgba(255, 255, 255, 0.97)
    width: 100vw
    height: 2.4em

    a
      margin-left: $main-margin
      margin-right: $main-margin

      img
        margin-top: 0.5em

  .footer
    font-size: 0.8em

    a
      color: $title-color
      text-decoration: none

  #safari-footer
    height: 50px
    background: transparent
    width: 100%

  .main-mobile
    margin: $main-margin

  .main-desktop
    overflow: hidden
    display: flex
    justify-content: center
    align-items: center
    width: 100%
    height: 100%

  .main-svg
    width: 100%

    #PreventClickarea, #CarsClickarea, #HealthClickarea1, #HealthClickarea2, #IndustryClickarea1, #IndustryClickarea2, #PreventClickarea, #EffectClickarea, #AntennaClickarea
      cursor: pointer

  .desktop-header
    background: linear-gradient(90deg, rgba(249, 245, 245, 0.8) 0%, rgba(255, 255, 255, 0.84) 50%, rgba(255, 255, 255, 1) 100%)
    position: absolute
    top: 0
    left: 0
    width: 100%
    height: 10vh
    display: flex
    justify-content: space-between
    align-items: center

    .main-header
      font-size: 2.5em
      margin-left: 1em

    img
      max-height: 100%

  .desktop-footer
    a:first-child
      margin-right: auto
      margin-left: 2em

    background: rgb(249, 245, 245)
    background: linear-gradient(90deg, rgba(249, 245, 245, 0.8) 0%, rgba(249, 245, 245, 0.84) 50%, rgba(249, 245, 245, 1) 100%)
    position: absolute
    bottom: 0
    left: 0
    width: 100%
    height: 5vh
    display: flex
    justify-content: flex-end
    align-items: center
    font-size: 0.8em

    a
      margin-right: 2em
      display: flex
      text-decoration: none
      color: $title-color
      cursor: pointer

  .boxes
    position: absolute
    display: flex
    bottom: 6%
    right: 3%
    align-items: end
    justify-content: space-between

    div
      background: white
      opacity: 0.9
      box-shadow: 1px 1px 2px
      font-size: 0.9em

    .boxes-gap
      width: $main-margin

  .hint-box
    max-width: 370px
    max-width: max(370px, 20vw)

    p
      margin: $main-margin

    a
      color: $subtitle-color
      text-decoration: none
      cursor: pointer

    img
      margin-right: $desktop-main-margin
      margin-top: $desktop-main-margin

  .legend-box
    max-width: 234px
    line-height: 14pt
    h2
      font-size: 1.1em
      line-height: 14pt
    h2, ul, p
      margin-left: $main-margin - 10px
      margin-right: $main-margin - 10px

    ul
      padding-left: 15px

  .info-overlay.info-left
    left: 10%

  .info-overlay.info-right
    right: 10%

  .info-overlay
    background: white
    position: absolute
    top: 11vh
    max-height: 83vh
    display: flex
    flex-direction: column
    width: 380px
    width: min(35vw, 380px)
    box-shadow: 1px 1px 2px
    z-index: 100

    a
      cursor: pointer

    .info-header
      h2, img
        font-size: 1.2em
        margin: $desktop-main-margin

      .sub-image
        margin-left: 0
        margin-right: 0
        margin-bottom: 0

    .info-content
      font-size: 0.9em
      scrollbar-color: $subtitle-color #6666662e
      scrollbar-width: thin
      overflow-y: auto

      > *
        margin-left: $main-margin
        margin-right: $main-margin

      ul
        padding-left: 25px

    .info-content::-webkit-scrollbar
      width: 7px

    .info-content::-webkit-scrollbar-track
      background: #6666662e

    .info-content::-webkit-scrollbar-thumb
      background-color: $subtitle-color

</style>
